// The platform's view of its companies (D-182, D-183): which segment each is in, the order to look at them, and their
// numbers and their team's phones in words. Counts only: nothing here is a company's customer, call or note.
import { INTEGRATIONS } from "./integrations";
import { phoneReadiness } from "./readiness";
import { ago } from "./teamHealth";
import type { AddOns, CompanyHealth, CompanyIntegration, CompanyStatus, CompanyTeamMember } from "./types";

export type Segment = "attention" | "onboarding" | "healthy" | "internal" | "suspended";

/** The Customers page's segments, in the order they are shown. */
export const SEGMENTS: { key: Segment | "all"; label: string }[] = [
  { key: "attention", label: "Needs attention" },
  { key: "onboarding", label: "Onboarding" },
  { key: "healthy", label: "Healthy" },
  { key: "internal", label: "Voxowa's own" },
  { key: "suspended", label: "Suspended" },
  { key: "all", label: "All" },
];

export const STATUS_LABEL: Record<CompanyStatus, string> = {
  at_risk: "At risk",
  watch: "Watch",
  onboarding: "Onboarding",
  healthy: "Healthy",
  suspended: "Suspended",
};

/** The badge tone for each status. */
export const STATUS_TONE: Record<CompanyStatus, string> = {
  at_risk: "danger",
  watch: "warm",
  onboarding: "brand",
  healthy: "ok",
  suspended: "",
};

/** Voxowa's own company is its own segment, whatever its status; at risk and watch both need attention. */
export function segmentOf(c: CompanyHealth): Segment {
  if (c.internal) return "internal";
  if (c.status === "suspended") return "suspended";
  if (c.status === "at_risk" || c.status === "watch") return "attention";
  return c.status;
}

export function inSegment(companies: CompanyHealth[], segment: Segment | "all"): CompanyHealth[] {
  return segment === "all" ? companies : companies.filter((c) => segmentOf(c) === segment);
}

const GRAVITY: Record<CompanyStatus, number> = { at_risk: 0, watch: 1, onboarding: 2, healthy: 3, suspended: 4 };

/** The order to look at them: at risk first, then watch; a company in a known quiet spell after the others of its
 * status; then the one with more reasons; then by name. */
export function byAttention(a: CompanyHealth, b: CompanyHealth): number {
  return GRAVITY[a.status] - GRAVITY[b.status] || Number(a.quiet) - Number(b.quiet) ||
    b.reasons.length - a.reasons.length || a.name.localeCompare(b.name);
}

/** How many companies of each status, leaving out Voxowa's own. */
export function statusCounts(companies: CompanyHealth[]): Record<CompanyStatus, number> {
  const counts: Record<CompanyStatus, number> = { at_risk: 0, watch: 0, onboarding: 0, healthy: 0, suspended: 0 };
  for (const c of companies) if (!c.internal) counts[c.status]++;
  return counts;
}

/** "4 of 6": the onboarding checklist. */
export function checklistProgress(c: CompanyHealth): { done: number; total: number; next: string | null } {
  const done = c.checklist.filter((x) => x.done).length;
  return { done, total: c.checklist.length, next: c.checklist.find((x) => !x.done)?.label ?? null };
}

/** "+12%", "−34%", or null with nothing usual to compare with. */
export function change(now: number, usual: number | null): string | null {
  if (usual === null || usual <= 0) return null;
  const pct = Math.round((100 * (now - usual)) / usual);
  return pct === 0 ? "as usual" : pct > 0 ? `+${pct}%` : `−${-pct}%`;
}

/** "74%", or "–" with nothing done. */
export function share(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((100 * part) / whole)}%` : "–";
}

export const USES_LABEL: Record<CompanyTeamMember["uses"], string> = {
  both: "Phone and dashboard",
  phone: "Phone only",
  dashboard: "Dashboard only",
  neither: "Neither this week",
};

/** "Samsung Galaxy M34 · Android 14 · app 1.4.0". */
export function phoneLine(m: CompanyTeamMember): string {
  if (!m.phone) return "No phone";
  return [
    m.phone.model ?? "Unknown phone",
    m.phone.android ? `Android ${m.phone.android}` : null,
    m.phone.app_version ? `app ${m.phone.app_version}` : null,
  ].filter(Boolean).join(" · ");
}

/** What may be wrong with this person's phone, for support: what the plan needs and the phone does not allow, what
 * would help, a sync problem, files stuck, or a phone that has not synced in a day. */
export function teamProblems(m: CompanyTeamMember, plan: AddOns, now: Date): string[] {
  if (!m.phone) return m.role === "caller" ? ["Has not signed in on a phone yet"] : [];
  const problems: string[] = [];
  const readiness = phoneReadiness(plan, m.phone.permissions);
  if (!readiness.reported) problems.push("Its app does not report what the phone allows: an old version");
  problems.push(...readiness.problems, ...readiness.advice);
  if (m.phone.sync_problem) problems.push(m.phone.sync_problem);
  if ((m.phone.files_waiting ?? 0) >= 20) problems.push(`${m.phone.files_waiting} recordings waiting to be sent`);
  const synced = m.phone.last_sync_at ? new Date(m.phone.last_sync_at) : null;
  if (!synced || now.getTime() - synced.getTime() > 24 * 3_600_000) {
    problems.push(`Last synced ${ago(synced, now)}`);
  }
  return problems;
}

/** The app versions on a team's phones, newest first, with how many phones run each: who has not updated. */
export function appVersions(team: CompanyTeamMember[]): { version: string; phones: number }[] {
  const counts = new Map<string, number>();
  for (const m of team) {
    if (!m.phone) continue;
    const v = m.phone.app_version ?? "unknown";
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const parts = (v: string) => v.split(/[.+]/).map((n) => Number(n) || 0);
  return [...counts].map(([version, phones]) => ({ version, phones })).sort((a, b) => {
    const [x, y] = [parts(a.version), parts(b.version)];
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      if ((y[i] ?? 0) !== (x[i] ?? 0)) return (y[i] ?? 0) - (x[i] ?? 0);
    }
    return 0;
  });
}

/** An integration's name, as the catalogue gives it, or its key for one the catalogue no longer lists. */
export function integrationName(key: string): string {
  return INTEGRATIONS.find((i) => i.key === key)?.name ?? key;
}

/** Its logo, from the catalogue, or null for one it no longer lists. */
export function integrationLogo(key: string): string | null {
  return INTEGRATIONS.find((i) => i.key === key)?.logo ?? null;
}

/** What it brings in: calls for a phone system, leads for everything else. */
function unit(key: string): "call" | "lead" {
  return INTEGRATIONS.find((i) => i.key === key)?.category === "telephony" ? "call" : "lead";
}

/** An integration's state in words, with its badge tone, for the platform (D-184). */
export function integrationState(i: CompanyIntegration): { label: string; tone: string } {
  if (i.status === "error" || (i.status === "connected" && i.error)) return { label: "Problem", tone: "danger" };
  if (i.status === "waiting") return { label: "Waiting for its first lead", tone: "warm" };
  if (i.status === "paused") return { label: "Paused", tone: "" };
  if (i.status === "disconnected") return { label: "Turned off", tone: "" };
  return { label: "Connected", tone: "ok" };
}

/** "54 leads this week, 84 in all · 1 Page · last lead 2 h ago". */
export function integrationActivity(i: CompanyIntegration, now: Date): string {
  const u = unit(i.key);
  const parts = [`${i.received_7} ${i.received_7 === 1 ? u : `${u}s`} this week, ${i.received} in all`];
  if (i.unread_7 > 0) parts.push(`${i.unread_7} not brought in`);
  if (i.pages > 0) parts.push(`${i.pages} ${i.pages === 1 ? "Page" : "Pages"}`);
  if (i.last_at) parts.push(`last ${u} ${ago(i.last_at, now)}`);
  return parts.join(" · ");
}

/** The integrations a company uses, for the Customers list: connected ones and those with a problem, not those turned
 * off. */
export function integrationsInUse(c: CompanyHealth): CompanyIntegration[] {
  return c.integrations.filter((i) => i.status !== "disconnected");
}
